import { ForwardedRef, forwardRef, useMemo } from "react";
import Select, {
  GroupBase,
  OptionProps,
  SelectInstance,
  components,
} from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import { AttributeType } from "__generated__/gql/graphql";
import useSelectStyles from "utils/useSelectStyles";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useFetchOptions from "components/Shared/Select/useFetchOptions";
import { useBrandLazyQuery } from "utils/apollo/hooks/brand";
import AttributeIsCertificationIcon from "components/Shared/AttributeIsCertificationIcon";

const optionFragment = gql(/* GraphQL */ `
  fragment AttributeSelectOption on Attribute {
    id
    name
    category
    isCertification
  }
`);

const valuesQuery = gql(/* GraphQL */ `
  query AttributeSelectValue($ids: [UUID!]!) {
    attributesList(filter: { id: { in: $ids } }) {
      ...AttributeSelectOption
    }
  }
`);

const optionsQuery = gql(/* GraphQL */ `
  query AttributeSelectOptions($attributeType: AttributeType!) {
    attributesList(
      filter: {
        isArchived: { equalTo: false }
        attributeType: { equalTo: $attributeType }
      }
      orderBy: NAME_ASC
    ) {
      ...AttributeSelectOption
    }
  }
`);

export type AttributeSelectOption = DocumentType<typeof optionFragment>;
type AttributeSelectOptionGroup = GroupBase<AttributeSelectOption>;
const UNCATEGORIZED_GROUP_KEY = "__UNCATEGORIZED__";

type Props = Omit<
  StateManagerProps<AttributeSelectOption, true>,
  "onChange" | "value"
> &
  IdValueSelectProps<true> & {
    attributeType: AttributeType;
    noOptionsMessageLabel: string;
    placeholderLabel: string;
  };

export type AttributeSelectWrapperProps = Omit<
  Props,
  "attributeType" | "noOptionsMessageLabel" | "placeholderLabel"
>;

function getOptions(data: DocumentType<typeof optionsQuery>) {
  return getFragmentData(optionFragment, data.attributesList);
}

function Option({
  data,
  children,
  ...props
}: OptionProps<AttributeSelectOption, true>) {
  return (
    <components.Option data={data} {...props}>
      {children}
      {data.isCertification ? (
        <AttributeIsCertificationIcon color="gray.600" />
      ) : undefined}
    </components.Option>
  );
}

function formatGroupLabel(group: AttributeSelectOptionGroup) {
  if (group.label === UNCATEGORIZED_GROUP_KEY) {
    return null;
  }
  return group.label;
}

function AttributeSelect(
  {
    attributeType,
    noOptionsMessageLabel,
    onChange,
    placeholderLabel,
    value: rawValue,
    isLoading,
    ...rest
  }: Props,
  ref: ForwardedRef<SelectInstance<AttributeSelectOption, true>>,
) {
  const styleProps = useSelectStyles<AttributeSelectOption, true>();

  const idValueProps = useIdValue<
    AttributeSelectOption,
    true,
    typeof valuesQuery
  >({
    getOptionsFromData: getOptions,
    getVariables: (ids) => ({ ids: ids.map(String) }),
    isMulti: true,
    labelKey: "name",
    onChange,
    query: valuesQuery,
    value: rawValue,
  });

  const lazyQuery = useBrandLazyQuery(optionsQuery, {
    variables: { attributeType },
  });

  const fetchOptionsProps = useFetchOptions({
    getOptions,
    lazyQuery,
    menuIsOpen: rest.menuIsOpen,
  });

  const groupedOptions = useMemo<
    AttributeSelectOptionGroup[] | undefined
  >(() => {
    if (!fetchOptionsProps.options?.length) return undefined;

    const groupedByCategory = fetchOptionsProps.options.reduce<
      Record<string, AttributeSelectOption[]>
    >((acc, option) => {
      const category = option.category?.trim() || UNCATEGORIZED_GROUP_KEY;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(option);
      return acc;
    }, {});

    return Object.keys(groupedByCategory)
      .sort((a, b) => {
        if (a === UNCATEGORIZED_GROUP_KEY) return -1;
        if (b === UNCATEGORIZED_GROUP_KEY) return 1;
        return a.localeCompare(b);
      })
      .map((label) => ({
        label,
        options: groupedByCategory[label],
      }));
  }, [fetchOptionsProps.options]);

  return (
    <Select<AttributeSelectOption, true>
      {...styleProps}
      {...idValueProps}
      {...fetchOptionsProps}
      isMulti
      ref={ref}
      options={groupedOptions}
      components={{ ...styleProps.components, Option }}
      formatGroupLabel={formatGroupLabel}
      styles={{
        ...styleProps.styles,
        groupHeading: (base) => ({
          ...base,
          _empty: { display: "none" },
          "&:empty": { display: "none" },
        }),
      }}
      isLoading={
        idValueProps.isLoading || fetchOptionsProps.isLoading || isLoading
      }
      placeholder={placeholderLabel}
      noOptionsMessage={() => noOptionsMessageLabel}
      {...rest}
    />
  );
}

export default forwardRef(AttributeSelect);
